import { FlashList } from '@shopify/flash-list';
import React, {
  ComponentProps,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TouchableOpacity } from 'react-native';

import { ContactType } from '@components/Invite/Contact';
import { Box, ShadowBox, Text } from '@components/Restyle';
import { FilterSectionHeader } from '@components/shared/FilterSectionHeader';
import Icon from '@components/shared/Icon/Icon';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { useContacts } from '@hooks/useContacts';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useMe from '@hooks/useMe';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];
type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  title: string;
  selectedOwners?: ContactType[];
  selectedMembers?: ContactType[];
  selectedTeamIds?: string[];
  onSelectOwner?: (owners: ContactType[]) => void;
  onSelectMember?: (members: ContactType[]) => void;
  onSelectTeamIds?: (teamIds: string[]) => void;
  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  openSection?: OpenSection;
  onOpenSectionChange?: (section: OpenSection) => void;
};

const ITEM_HEIGHT = 36;
const ESTIMATED_ITEM_SIZE = ITEM_HEIGHT;
const LIST_MAX_HEIGHT = ITEM_HEIGHT * 4;

type SectionKey = 'owner' | 'team' | 'member';
type OpenSection = SectionKey | null;

type FilterListRowProps = {
  label: string;
  checked: boolean;
  onPress: () => void;
};

const FilterListRow: React.FC<FilterListRowProps> = ({
  label,
  checked,
  onPress,
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <TouchableOpacity
      accessibilityRole='button'
      accessibilityLabel={label}
      onPress={onPress}>
      <Box
        height={ITEM_HEIGHT}
        px='m'
        flexDirection='row'
        alignItems='center'
        backgroundColor={hovered ? 'grey01' : 'white'}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}>
        <Box marginRight='xs'>
          <CheckBoxButton checked={checked} />
        </Box>
        <Text
          flex={1}
          numberOfLines={1}
          variant='webBodySecondary'
          color='textPrimary'>
          {label}
        </Text>
      </Box>
    </TouchableOpacity>
  );
};

export const PopupGlobalSearchProjectsFilterButtonWeb: React.FC<Props> = ({
  title,
  selectedOwners = [],
  selectedMembers = [],
  selectedTeamIds = [],
  onSelectOwner,
  onSelectMember,
  onSelectTeamIds,
  boxProps: { position, offset },
  visible: visibleProp,
  onVisibleChange,
  openSection: openSectionProp,
  onOpenSectionChange,
}) => {
  const { t } = useTranslation('shared');
  const { contacts } = useContacts();
  const { me } = useMe();
  const { teams } = useListTeamsFromQuery();

  const listUsersToContacts = useMemo(
    () =>
      convertUsersToContacts(
        !me || contacts.some((c) => c.id === me.id)
          ? contacts
          : [...contacts, me]
      ).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [contacts, me]
  );

  const sortedTeams = useMemo(
    () => [...(teams || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [teams]
  );

  const [visibleLocal, setVisibleLocal] = useState(false);
  const isControlled = visibleProp !== undefined;
  const visible = isControlled ? visibleProp : visibleLocal;
  const setVisible = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const next = typeof value === 'function' ? value(visible) : value;
      if (isControlled) {
        onVisibleChange?.(next);
      } else {
        setVisibleLocal(next);
      }
    },
    [isControlled, visible, onVisibleChange]
  );
  const [openSectionLocal, setOpenSectionLocal] = useState<OpenSection>(null);
  const openSection =
    openSectionProp !== undefined ? openSectionProp : openSectionLocal;
  const setOpenSection = useCallback(
    (section: OpenSection) => {
      if (onOpenSectionChange) {
        onOpenSectionChange(section);
      } else {
        setOpenSectionLocal(section);
      }
    },
    [onOpenSectionChange]
  );
  const [mouseOver, setMouseOver] = useState(false);
  const popupRef = useRef<HTMLDivElement>();

  const badgeNumber =
    selectedOwners.length + selectedMembers.length + selectedTeamIds.length;

  const isEventPointInArea = (
    areaRef: RefObject<HTMLDivElement>,
    event: MouseEvent
  ) => {
    const rect = areaRef.current?.getBoundingClientRect();
    return (
      rect &&
      event.x > rect.x &&
      event.x < rect.x + rect.width &&
      event.y > rect.y &&
      event.y < rect.y + rect.height
    );
  };

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!isEventPointInArea(popupRef, event)) {
      setVisible(false);
    }
  }, []);

  const ref = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const adjustBounds = (
    popupPosition: Props['boxProps']['position'] = 'bottom',
    popupOffset: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    const [offsetLeftRight, offsetTopBottom] =
      typeof popupOffset === 'number'
        ? [popupOffset, popupOffset]
        : popupOffset;

    const normalizedPosition = (typeof popupPosition !== 'string' &&
      popupPosition) ||
      ((popupPosition === 'left' || popupPosition === 'right') && [
        popupPosition,
        'bottom',
      ]) ||
      ((popupPosition === 'top' || popupPosition === 'bottom') && [
        'left',
        popupPosition,
      ]) || ['left', 'bottom'];

    const [leftRight, topBottom] = normalizedPosition;

    return {
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom,
      left:
        leftRight === 'left'
          ? client.left + client.width - popupLayout.width - offsetLeftRight
          : client.left + offsetLeftRight,
    };
  };

  const adjustedBounds =
    ref.current &&
    layout &&
    adjustBounds(position, offset, ref.current.getBoundingClientRect(), layout);

  const onPressOwner = (contact: ContactType) => {
    const isSelected = selectedOwners.some((item) => item.id === contact.id);
    onSelectOwner?.(
      isSelected
        ? selectedOwners.filter((item) => item.id !== contact.id)
        : [...selectedOwners, contact]
    );
  };

  const onPressMember = (contact: ContactType) => {
    const isSelected = selectedMembers.some((item) => item.id === contact.id);
    onSelectMember?.(
      isSelected
        ? selectedMembers.filter((item) => item.id !== contact.id)
        : [...selectedMembers, contact]
    );
  };

  const onPressTeam = (teamId: string) => {
    const isSelected = selectedTeamIds.includes(teamId);
    onSelectTeamIds?.(
      isSelected
        ? selectedTeamIds.filter((item) => item !== teamId)
        : [...selectedTeamIds, teamId]
    );
  };

  const renderListSection = () => {
    switch (openSection) {
      case null:
        return null;
      case 'owner':
        return (
          <Box style={{ maxHeight: LIST_MAX_HEIGHT }}>
            <FlashList
              data={listUsersToContacts}
              extraData={selectedOwners}
              estimatedItemSize={ESTIMATED_ITEM_SIZE}
              keyExtractor={(item) => item.id}
              showsVerticalScrollIndicator={true}
              renderItem={({ item }) => (
                <FilterListRow
                  label={item.displayName || item.name || t('noName')}
                  checked={selectedOwners.some((owner) => owner.id === item.id)}
                  onPress={() => onPressOwner(item)}
                />
              )}
            />
          </Box>
        );
      case 'team':
        return (
          <Box style={{ maxHeight: LIST_MAX_HEIGHT }}>
            <FlashList
              data={sortedTeams}
              extraData={selectedTeamIds}
              estimatedItemSize={ESTIMATED_ITEM_SIZE}
              keyExtractor={(item) => item.id}
              showsVerticalScrollIndicator={true}
              renderItem={({ item }) => (
                <FilterListRow
                  label={item.name}
                  checked={selectedTeamIds.includes(item.id)}
                  onPress={() => onPressTeam(item.id)}
                />
              )}
            />
          </Box>
        );
      case 'member':
        return (
          <Box style={{ maxHeight: LIST_MAX_HEIGHT }}>
            <FlashList
              data={listUsersToContacts}
              extraData={selectedMembers}
              estimatedItemSize={ESTIMATED_ITEM_SIZE}
              keyExtractor={(item) => item.id}
              showsVerticalScrollIndicator={true}
              renderItem={({ item }) => (
                <FilterListRow
                  label={item.displayName || item.name || t('noName')}
                  checked={selectedMembers.some(
                    (member) => member.id === item.id
                  )}
                  onPress={() => onPressMember(item)}
                />
              )}
            />
          </Box>
        );
    }
  };

  const backgroundColor =
    mouseOver || badgeNumber > 0 || visible ? 'grey01' : 'white';

  const doClearAll = () => {
    onSelectMember?.([]);
    onSelectTeamIds?.([]);
    onSelectOwner?.([]);
  };
  const popupContent = (
    <ShadowBox
      width={380}
      borderWidth={1}
      borderColor='grey02'
      borderRadius='xs'
      backgroundColor='white'
      variant='base'>
      <Box flex={1}>
        <FilterSectionHeader
          showTopBorder={false}
          title={t('owner')}
          badgeNumber={selectedOwners.length}
          isExpanded={openSection === 'owner'}
          onPress={() =>
            setOpenSection(openSection === 'owner' ? null : 'owner')
          }
          height={52}
        />
        {openSection === 'owner' && renderListSection()}
        <FilterSectionHeader
          title={t('team')}
          badgeNumber={selectedTeamIds.length}
          isExpanded={openSection === 'team'}
          onPress={() => setOpenSection(openSection === 'team' ? null : 'team')}
          height={52}
        />
        {openSection === 'team' && renderListSection()}
        <FilterSectionHeader
          title={t('member')}
          badgeNumber={selectedMembers.length}
          isExpanded={openSection === 'member'}
          onPress={() =>
            setOpenSection(openSection === 'member' ? null : 'member')
          }
          height={52}
        />
        {openSection === 'member' && renderListSection()}
        <Box
          pt='m'
          px='m'
          mb='m'
          height={ITEM_HEIGHT}
          borderTopWidth={1}
          borderTopColor='grey02'
          alignItems='center'
          justifyContent='center'>
          <TouchableOpacity
            disabled={badgeNumber === 0}
            accessibilityRole='button'
            accessibilityLabel={t('clearAll')}
            onPress={() => {
              doClearAll();
            }}>
            <Text
              variant='webBodySecondary'
              color={badgeNumber === 0 ? 'grey04' : 'textPrimary'}>
              {t('clearAll')}
            </Text>
          </TouchableOpacity>
        </Box>
      </Box>
    </ShadowBox>
  );

  return (
    <Box ref={ref}>
      <TouchableOpacity
        accessibilityLabel={title}
        onPress={() => setVisible((current) => !current)}>
        <Box
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
          flexDirection='row'
          px='m'
          py='xs'
          borderRadius='xs'
          backgroundColor={backgroundColor}
          alignItems='center'
          justifyContent='center'
          onMouseEnter={() => setMouseOver(true)}
          onMouseLeave={() => setMouseOver(false)}>
          <Icon name='AlignCenterDesc' variant='s' color='black'></Icon>
          <Text variant='webSmall' color='black' ml='xs'>
            {title}
          </Text>
          {badgeNumber > 0 ? (
            <Box
              marginLeft='xs'
              minWidth={20}
              height={20}
              px='xxs'
              alignItems='center'
              justifyContent='center'
              backgroundColor='greenSecondary'
              borderRadius='s'>
              <Text variant='webBodyHintSubline' color='white'>
                {badgeNumber}
              </Text>
            </Box>
          ) : (
            <></>
          )}
          {badgeNumber > 0 ? (
            <TouchableOpacity
              accessibilityRole='button'
              accessibilityLabel={t('clearAll')}
              onPress={() => {
                doClearAll();
              }}>
              <Box
                width={16}
                height={16}
                marginLeft='xs'
                alignItems='center'
                justifyContent='center'>
                <Icon variant='xxxs' name='Close' color='black' />
              </Box>
            </TouchableOpacity>
          ) : (
            <></>
          )}
        </Box>
      </TouchableOpacity>

      {!layout && (
        <Box
          pointerEvents='none'
          style={{ position: 'absolute', left: -9999, opacity: 0 }}
          onLayout={({ nativeEvent: { layout: nextLayout } }) => {
            setLayout((current) => current ?? nextLayout);
          }}>
          {popupContent}
        </Box>
      )}

      <Modal
        visible={visible && !!layout}
        transparent
        onRequestClose={() => setVisible(false)}
        onDismiss={() => setVisible(false)}>
        <Box ref={popupRef} style={adjustedBounds} width={layout?.width}>
          {popupContent}
        </Box>
      </Modal>
    </Box>
  );
};
